import { useEffect, useState, type ReactNode } from 'react';
import { getLlmMeta } from '../lib/llmModels';

export type SearchModalMentionStatus = 'mentioned' | 'indirect' | 'none';

export type SearchResponseModalBrand = {
  domain: string;
  name: string;
  faviconUrl: string;
  isWorkspaceBrand?: boolean;
};

export type SearchResponseModalCitation = {
  url: string;
  domain: string;
  label?: string | null;
  title?: string | null;
};

export type SearchResponseModalItem = {
  id: string;
  promptText: string;
  createdAt: string;
  model: string;
  responseText: string;
  brands: SearchResponseModalBrand[];
  citations: SearchResponseModalCitation[];
  responseLinks?: SearchResponseModalCitation[];
  mentionStatus: SearchModalMentionStatus;
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const isImageUrl = (value: string) =>
  /^https?:\/\/\S+\.(png|jpe?g|gif|webp|avif|svg)(\?\S*)?$/i.test(value);

type InlineToken =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; value: string; href: string; isBare?: boolean }
  | { type: 'citation-ref'; value: string; href: string }
  | { type: 'image'; alt: string; src: string };

const parseInlineTokens = (
  value: string,
  citations: SearchResponseModalCitation[] = []
): InlineToken[] => {
  const tokens: InlineToken[] = [];
  const pattern =
    /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\[(\d+)\](?!\()|((?:https?:\/\/|www\.)[^\s<]+)/g;
  let lastIndex = 0;

  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      tokens.push({ type: 'text', value: value.slice(lastIndex, index) });
    }

    if (match[1] && match[2]) {
      tokens.push({ type: 'image', alt: match[1], src: match[2] });
    } else if (match[3] && match[4]) {
      tokens.push({ type: 'link', value: match[3], href: match[4], isBare: false });
    } else if (match[5]) {
      tokens.push({ type: 'strong', value: match[5] });
    } else if (match[6]) {
      tokens.push({ type: 'strong', value: match[6] });
    } else if (match[7]) {
      tokens.push({ type: 'code', value: match[7] });
    } else if (match[8]) {
      const citationIndex = parseInt(match[8], 10) - 1;
      const citation = citations[citationIndex];
      if (citation?.url) {
        tokens.push({ type: 'citation-ref', value: `[${match[8]}]`, href: citation.url });
      } else {
        tokens.push({ type: 'text', value: match[0] });
      }
    } else if (match[9]) {
      const href = match[9].startsWith('www.') ? `https://${match[9]}` : match[9];
      if (isImageUrl(href)) {
        tokens.push({ type: 'image', alt: 'Search image', src: href });
      } else {
        tokens.push({ type: 'link', value: match[9], href, isBare: true });
      }
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < value.length) {
    tokens.push({ type: 'text', value: value.slice(lastIndex) });
  }

  return tokens;
};

const renderInlineContent = (
  value: string,
  keyPrefix: string,
  citations: SearchResponseModalCitation[] = []
) =>
  parseInlineTokens(value, citations).map((token, index) => {
    const key = `${keyPrefix}-${index}`;

    if (token.type === 'text') return <span key={key}>{token.value}</span>;
    if (token.type === 'strong') return <strong key={key}>{token.value}</strong>;
    if (token.type === 'code') return <code key={key} className="search-inline-code">{token.value}</code>;
    if (token.type === 'link') {
      return (
        <a
          key={key}
          href={token.href}
          target="_blank"
          rel="noreferrer"
          className={token.isBare ? 'search-inline-link-pill' : undefined}
        >
          {token.value}
        </a>
      );
    }
    if (token.type === 'citation-ref') {
      return (
        <a
          key={key}
          href={token.href}
          target="_blank"
          rel="noreferrer"
          className="search-inline-citation-ref"
        >
          {token.value}
        </a>
      );
    }
    return <img key={key} src={token.src} alt={token.alt} className="search-render-image" />;
  });

const isTableLine = (line: string) => /^\s*\|.+\|\s*$/.test(line);

const isTableSeparatorLine = (line: string) =>
  /^\s*\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(line);

const parseTableCells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

const renderRichResponse = (value: string, citations: SearchResponseModalCitation[] = []) => {
  const lines = value.replace(/\r\n/g, '\n').split('\n');
  const nodes: ReactNode[] = [];
  let index = 0;
  let blockKey = 0;

  const isListLine = (line: string) => /^(\s*)([-*+•]|\d+\.)\s+/.test(line);

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (/^(?:-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      nodes.push(<hr key={`divider-${blockKey++}`} className="search-render-divider" />);
      index += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      nodes.push(
        <pre key={`code-${blockKey++}`} className="search-detail-code">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    if (
      isTableLine(trimmed) &&
      index + 1 < lines.length &&
      isTableSeparatorLine(lines[index + 1].trim())
    ) {
      const headerCells = parseTableCells(trimmed);
      const bodyRows: string[][] = [];
      index += 2;

      while (index < lines.length && isTableLine(lines[index].trim())) {
        bodyRows.push(parseTableCells(lines[index].trim()));
        index += 1;
      }

      nodes.push(
        <div key={`table-wrap-${blockKey++}`} className="search-render-table-wrap">
          <table className="search-render-table">
            <thead>
              <tr>
                {headerCells.map((cell, cellIndex) => (
                  <th key={`table-head-${cellIndex}`}>
                    {renderInlineContent(cell, `table-head-${blockKey}-${cellIndex}`, citations)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={`table-row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`table-cell-${rowIndex}-${cellIndex}`}>
                      {renderInlineContent(cell, `table-cell-${blockKey}-${rowIndex}-${cellIndex}`, citations)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = Math.min(6, headingMatch[1].length);
      const content = renderInlineContent(headingMatch[2], `heading-${blockKey}`, citations);
      const key = `heading-${blockKey++}`;
      if (level === 1) nodes.push(<h1 key={key}>{content}</h1>);
      else if (level === 2) nodes.push(<h2 key={key}>{content}</h2>);
      else if (level === 3) nodes.push(<h3 key={key}>{content}</h3>);
      else if (level === 4) nodes.push(<h4 key={key}>{content}</h4>);
      else if (level === 5) nodes.push(<h5 key={key}>{content}</h5>);
      else nodes.push(<h6 key={key}>{content}</h6>);
      index += 1;
      continue;
    }

    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('>')) {
        quoteLines.push(lines[index].trim().replace(/^>\s?/, ''));
        index += 1;
      }
      nodes.push(
        <blockquote key={`quote-${blockKey++}`}>
          {quoteLines.map((quoteLine, quoteIndex) => (
            <p key={`quote-line-${quoteIndex}`}>
              {renderInlineContent(quoteLine, `quote-${blockKey}-${quoteIndex}`, citations)}
            </p>
          ))}
        </blockquote>
      );
      continue;
    }

    if (isListLine(trimmed)) {
      const listLines: string[] = [];
      const ordered = /^\d+\.\s+/.test(trimmed);
      while (index < lines.length && isListLine(lines[index].trim())) {
        listLines.push(lines[index].trim());
        index += 1;
      }

      const ListTag = ordered ? 'ol' : 'ul';
      nodes.push(
        <ListTag key={`list-${blockKey++}`}>
          {listLines.map((entry, itemIndex) => {
            const content = entry.replace(/^(\s*)([-*+•]|\d+\.)\s+/, '');
            return (
              <li key={`list-item-${itemIndex}`}>
                {renderInlineContent(content, `list-${blockKey}-${itemIndex}`, citations)}
              </li>
            );
          })}
        </ListTag>
      );
      continue;
    }

    const paragraphLines = [trimmed];
    index += 1;
    while (index < lines.length) {
      const next = lines[index].trim();
      if (
        !next ||
        next.startsWith('```') ||
        next.startsWith('>') ||
        /^#{1,6}\s+/.test(next) ||
        isListLine(next) ||
        (isTableLine(next) && index + 1 < lines.length && isTableSeparatorLine(lines[index + 1].trim()))
      ) {
        break;
      }
      paragraphLines.push(next);
      index += 1;
    }

    nodes.push(
      <p key={`paragraph-${blockKey++}`}>
        {renderInlineContent(paragraphLines.join(' '), `paragraph-${blockKey}`, citations)}
      </p>
    );
  }

  return nodes;
};

export function SearchResponseModal({
  item,
  onClose,
}: {
  item: SearchResponseModalItem | null;
  onClose: () => void;
}) {
  const [renderedItem, setRenderedItem] = useState<SearchResponseModalItem | null>(item);
  const [isVisible, setIsVisible] = useState(Boolean(item));
  const [isAnimating, setIsAnimating] = useState(Boolean(item));

  useEffect(() => {
    let frameA = 0;
    let frameB = 0;
    let timer = 0;

    if (item) {
      setRenderedItem(item);
      setIsVisible(true);
      frameA = window.requestAnimationFrame(() => {
        frameB = window.requestAnimationFrame(() => setIsAnimating(true));
      });
    } else if (renderedItem) {
      setIsAnimating(false);
      timer = window.setTimeout(() => {
        setIsVisible(false);
        setRenderedItem(null);
      }, 120);
    }

    return () => {
      if (frameA) window.cancelAnimationFrame(frameA);
      if (frameB) window.cancelAnimationFrame(frameB);
      if (timer) window.clearTimeout(timer);
    };
  }, [item, renderedItem]);

  useEffect(() => {
    if (!isVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose]);

  if (!isVisible || !renderedItem) return null;

  const modelMeta = getLlmMeta(renderedItem.model);
  const statusClass = renderedItem.mentionStatus === 'none' ? 'none' : 'mentioned';
  const statusLabel = renderedItem.mentionStatus === 'none' ? 'Not mentioned' : 'Mentioned';

  return (
    <div className={`rename-modal is-visible search-detail-modal-shell ${isAnimating ? 'is-open' : 'is-closing'}`}>
      <div className="white-background search-detail-modal">
        <div className="modal-close" onClick={onClose} style={{ cursor: 'pointer' }}>
          <div className="rotated-text">+</div>
        </div>

        <div className="search-detail-header">
          <div className="search-detail-header-top">
            <div className="text-size-medium text-weight-semibold">Search response</div>
            <div className={`search-mention-tag is-${statusClass}`}>{statusLabel}</div>
          </div>
          <div className="spacer-xxtiny"></div>
          <div className="text-size-regular text-weight-regular text-colour-dark-brown">
            “{renderedItem.promptText}”
          </div>
          <div className="search-detail-divider"></div>
          <div className="search-detail-supporting">
            <div className="search-detail-support-main">
              <div className="search-detail-meta-group">
                <div className="recent-search-model">
                  <div className="overview-model-badge">
                    {modelMeta.icon ? (
                      <img src={modelMeta.icon} alt={modelMeta.name} className="overview-model-badge-image" />
                    ) : (
                      modelMeta.short
                    )}
                  </div>
                  <span>{modelMeta.name}</span>
                </div>
                <div className="recent-search-date">{formatDate(renderedItem.createdAt)}</div>
              </div>
              <div className="search-detail-support-item is-brands">
                <span className="text-size-xsmall text-colour-alt-grey">Brands mentioned</span>
                {renderedItem.brands.length === 0 ? (
                  <span className="text-size-xsmall text-colour-light-grey">—</span>
                ) : (
                  <div className="search-detail-brands">
                    {renderedItem.brands.map((brand) => (
                      <div key={`modal-${renderedItem.id}-${brand.domain}`} className="search-detail-brand-pill">
                        <div className="search-brand-badge-image" style={{ backgroundImage: `url(${brand.faviconUrl})` }} />
                        <span>{brand.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="search-detail-divider"></div>
        </div>
        {renderedItem.responseLinks && renderedItem.responseLinks.length > 0 && (
          <div className="search-detail-citations">
            <span className="text-size-xsmall text-colour-alt-grey">Links in answer</span>
            <div className="search-detail-citation-list">
              {renderedItem.responseLinks.map((link) => (
                <a
                  key={`${renderedItem.id}-link-${link.url}-${link.label || ''}`}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="search-detail-citation-pill"
                >
                  {link.label || link.title || link.domain}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="search-detail-scroll">
          <div className="search-rendered-content-shell">
            <div className="search-rendered-content">
              {renderRichResponse(renderedItem.responseText, renderedItem.citations)}
            </div>
          </div>
        </div>
      </div>

      <div className="close-layer" onClick={onClose}></div>
    </div>
  );
}
